'use client';

/**
 * ExtractionGlassBoxPanel - OODA-L audit trail from DI extraction
 *
 * Shows the Conversation -> Message flow produced by PDF extraction.
 * Same rendering as TestGlassBoxPanel but fetches via data gateway
 * using conversation_sid from the extraction store.
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain vendor_portal
 */

import { useState, useEffect } from 'react';
import {
  Loader2, Eye, Compass, BrainCircuit, Zap, MessageSquare,
  CheckCircle2, XCircle, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import { api } from '@/lib/api/client';
import { useExtractionStore } from '@/lib/stores/extraction-store';

// ---------------------------------------------------------------------------
// Types (same shape as test-glass-box-panel)
// ---------------------------------------------------------------------------

interface AuditSection {
  type: 'kv_pairs' | 'stats' | 'product_match' | 'rules_list' | 'extraction' | 'text' | 'raw_json';
  label: string;
  data: Record<string, unknown>;
}

interface ConversationMessage {
  cdk: string | null;
  body: string | null;
  subject: string | null;
  strategy: string | null;
  author_name: string | null;
  step_entity: string | null;
  step_context: string | null;
  step_rules: string | null;
  step_entity_sections: AuditSection[];
  step_context_sections: AuditSection[];
  step_rules_sections: AuditSection[];
}

interface ConversationDetail {
  conversation_sid: string;
  subject_name: string | null;
  turn_count: number | null;
  success: boolean | null;
  summary: string | null;
  duration_ms: number | null;
  messages: ConversationMessage[];
}

// ---------------------------------------------------------------------------
// Phase config
// ---------------------------------------------------------------------------

const PHASE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  OBSERVE: Eye,
  ORIENT: Compass,
  DECIDE: BrainCircuit,
  ACT: Zap,
  SUBMIT: MessageSquare,
};

const PHASE_COLORS: Record<string, string> = {
  OBSERVE: 'text-blue-500 bg-blue-500/10',
  ORIENT: 'text-amber-500 bg-amber-500/10',
  DECIDE: 'text-purple-500 bg-purple-500/10',
  ACT: 'text-green-500 bg-green-500/10',
  SUBMIT: 'text-gray-500 bg-gray-500/10',
};

// ---------------------------------------------------------------------------
// Section card components
// ---------------------------------------------------------------------------

function KvPairsCard({ data, label }: { data: { items: { key: string; value: string }[] }; label: string }) {
  return (
    <div>
      <span className="text-[9px] font-semibold text-muted-foreground">{label}</span>
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 mt-0.5 text-[10px]">
        {data.items.map((item) => (
          <div key={item.key} className="flex gap-1">
            <span className="text-muted-foreground">{item.key}:</span>
            <span className="font-medium truncate">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsCard({ data, label }: { data: { items: { key: string; value: number; label: string }[] }; label: string }) {
  return (
    <div>
      <span className="text-[9px] font-semibold text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1.5 mt-0.5">
        {data.items.map((item) => (
          <Badge key={item.key} variant="secondary" className="text-[9px] font-mono gap-1">
            {item.label}: {item.value}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function ProductMatchCard({ data }: { data: { matched: unknown[]; unmatched: unknown[]; matched_count: number; total: number } }) {
  return (
    <div>
      <span className="text-[9px] font-semibold text-muted-foreground">
        Product Matching ({data.matched_count}/{data.total})
      </span>
      <div className="space-y-1 mt-0.5">
        {data.matched.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {data.matched.map((p, i) => (
              <Badge key={i} variant="secondary" className="text-[9px] bg-green-500/10 text-green-600">
                {typeof p === 'string' ? p : String((p as Record<string, unknown>)?.name ?? JSON.stringify(p))}
              </Badge>
            ))}
          </div>
        )}
        {data.unmatched.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {data.unmatched.map((p, i) => (
              <Badge key={i} variant="secondary" className="text-[9px] bg-red-500/10 text-red-600">
                {typeof p === 'string' ? p : String((p as Record<string, unknown>)?.name ?? JSON.stringify(p))}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RulesListCard({ data }: { data: { rules: { name: string; passed: boolean; action?: string; result?: string; value: unknown }[] } }) {
  return (
    <div>
      <span className="text-[9px] font-semibold text-muted-foreground">Rules</span>
      <div className="space-y-0.5 mt-0.5">
        {data.rules.map((rule, i) => (
          <div key={i} className="flex items-center gap-1.5 text-[10px]">
            {rule.passed ? (
              <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />
            ) : (
              <XCircle className="w-3 h-3 text-red-500 shrink-0" />
            )}
            <span className={cn('font-medium', rule.passed ? 'text-foreground' : 'text-red-600')}>
              {rule.name}
            </span>
            {rule.action && (
              <Badge variant="outline" className="text-[8px] px-1 py-0">{rule.action}</Badge>
            )}
            {rule.result && (
              <Badge
                variant="secondary"
                className={cn('text-[8px] px-1 py-0', {
                  'bg-green-500/10 text-green-600': rule.result === 'pass' || rule.result === 'passed',
                  'bg-yellow-500/10 text-yellow-600': rule.result === 'skip',
                  'bg-red-500/10 text-red-600': rule.result === 'fail' || rule.result === 'failed',
                })}
              >
                {rule.result}
              </Badge>
            )}
            {rule.value && (
              <span className="text-muted-foreground truncate">{String(rule.value)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ExtractionCard({ data }: { data: { products: Record<string, unknown>[] } }) {
  if (!data.products.length) return null;
  const keys = Object.keys(data.products[0]).filter((k) => k !== '__typename');
  return (
    <div>
      <span className="text-[9px] font-semibold text-muted-foreground">Extracted Products</span>
      <div className="overflow-auto mt-0.5">
        <table className="w-full text-[9px]">
          <thead>
            <tr className="border-b border-border">
              {keys.map((k) => (
                <th key={k} className="text-left py-0.5 px-1 font-semibold text-muted-foreground">{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.products.map((p, i) => (
              <tr key={i} className="border-b border-border/50">
                {keys.map((k) => (
                  <td key={k} className="py-0.5 px-1 truncate max-w-[120px]">{String(p[k] ?? '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TextCard({ data, label }: { data: { text: string; truncated: boolean }; label: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <div
        className="flex items-center gap-1 cursor-pointer"
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <span className="text-[9px] font-semibold text-muted-foreground">{label}</span>
        <span className="text-[9px] text-muted-foreground/60">({data.text.length} chars)</span>
      </div>
      {expanded && (
        <pre className="text-[9px] font-mono bg-muted/30 p-1.5 rounded mt-0.5 overflow-auto max-h-48 whitespace-pre-wrap">
          {data.text}
        </pre>
      )}
    </div>
  );
}

function RawJsonCard({ data, label }: { data: { raw: string }; label: string }) {
  return (
    <div>
      <span className="text-[9px] font-semibold text-muted-foreground">{label}</span>
      <pre className="text-[9px] font-mono bg-muted/30 p-1.5 rounded mt-0.5 overflow-auto max-h-24">
        {data.raw}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section dispatcher
// ---------------------------------------------------------------------------

function AuditSectionCard({ section }: { section: AuditSection }) {
  const { type, label, data } = section;
  switch (type) {
    case 'kv_pairs':
      return <KvPairsCard data={data as { items: { key: string; value: string }[] }} label={label} />;
    case 'stats':
      return <StatsCard data={data as { items: { key: string; value: number; label: string }[] }} label={label} />;
    case 'product_match':
      return <ProductMatchCard data={data as { matched: unknown[]; unmatched: unknown[]; matched_count: number; total: number }} />;
    case 'rules_list':
      return <RulesListCard data={data as { rules: { name: string; passed: boolean; value: unknown }[] }} />;
    case 'extraction':
      return <ExtractionCard data={data as { products: Record<string, unknown>[] }} />;
    case 'text':
      return <TextCard data={data as { text: string; truncated: boolean }} label={label} />;
    case 'raw_json':
      return <RawJsonCard data={data as { raw: string }} label={label} />;
    default:
      return <RawJsonCard data={{ raw: JSON.stringify(data, null, 2) }} label={label} />;
  }
}

function StepSections({ sections, fallback }: { sections: AuditSection[]; fallback: string | null }) {
  if (sections && sections.length > 0) {
    return (
      <>
        {sections.map((s, i) => (
          <AuditSectionCard key={i} section={s} />
        ))}
      </>
    );
  }
  if (fallback) return <DetailBlock label="Data" value={fallback} />;
  return null;
}

function DetailBlock({ label, value }: { label: string; value: string }) {
  let formatted = value;
  try {
    const parsed = JSON.parse(value);
    formatted = JSON.stringify(parsed, null, 2);
  } catch {
    // Not JSON, show as-is
  }

  return (
    <div>
      <span className="text-[9px] font-semibold text-muted-foreground">{label}</span>
      <pre className="text-[9px] font-mono bg-muted/30 p-1.5 rounded mt-0.5 overflow-auto max-h-24">
        {formatted}
      </pre>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export function ExtractionGlassBoxPanel() {
  const conversationSid = useExtractionStore((s) => s.conversationSid);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedMessages, setExpandedMessages] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!conversationSid) {
      setDetail(null);
      return;
    }
    const fetchGlassBox = async () => {
      setIsLoading(true);
      try {
        const res = await api.getStrategyData<{ conversation: ConversationDetail | null }>(
          'conversation_glass_box',
          { conversation_sid: conversationSid }
        );
        if (res.success && res.data?.conversation) {
          setDetail(res.data.conversation);
        } else {
          setDetail(null);
        }
      } catch {
        setDetail(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGlassBox();
  }, [conversationSid]);

  const toggleMessage = (idx: number) => {
    setExpandedMessages((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  if (!conversationSid) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <Eye className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No extraction yet</p>
        <p className="text-xs mt-1">Upload and extract a PDF to see the OODA-L audit trail</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading extraction trail...
      </div>
    );
  }

  if (!detail || !detail.messages.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground p-4">
        <Eye className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No audit trail available</p>
        <p className="text-xs mt-1 font-mono text-muted-foreground/60">{conversationSid}</p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-muted-foreground" />
          <h4 className="text-xs font-semibold text-muted-foreground">EXTRACTION TRAIL</h4>
        </div>
        <div className="flex items-center gap-2">
          {detail.success != null && (
            <Badge
              variant="secondary"
              className={cn('text-[9px] font-semibold', detail.success
                ? 'bg-green-500/15 text-green-600'
                : 'bg-red-500/15 text-red-600'
              )}
            >
              {detail.success ? (
                <><CheckCircle2 className="w-3 h-3 mr-0.5" /> COMPLETED</>
              ) : (
                <><XCircle className="w-3 h-3 mr-0.5" /> INCOMPLETE</>
              )}
            </Badge>
          )}
          {detail.turn_count != null && (
            <Badge variant="secondary" className="text-[9px]">
              {detail.turn_count} turns
            </Badge>
          )}
          <Badge variant="outline" className="text-[9px] font-mono">
            {detail.messages.length} msgs
          </Badge>
        </div>
      </div>

      {/* Subject + SID */}
      {detail.subject_name && (
        <p className="text-xs text-muted-foreground border-l-2 border-primary/30 pl-2">
          {detail.subject_name}
        </p>
      )}
      <p className="text-[9px] font-mono text-muted-foreground/60 select-all">
        {detail.conversation_sid}
      </p>

      {/* Messages */}
      <div className="space-y-2">
        {detail.messages.map((msg, idx) => {
          const phase = msg.subject ?? 'UNKNOWN';
          const PhaseIcon = PHASE_ICONS[phase] ?? MessageSquare;
          const colors = PHASE_COLORS[phase] ?? 'text-muted-foreground bg-muted/50';
          const isExpanded = expandedMessages.has(idx);

          return (
            <div
              key={idx}
              className="rounded border border-border overflow-hidden cursor-pointer"
              onClick={() => toggleMessage(idx)}
            >
              {/* Message header */}
              <div className={cn('flex items-center gap-2 px-2 py-1.5', colors.split(' ')[1])}>
                <PhaseIcon className={cn('w-3.5 h-3.5 shrink-0', colors.split(' ')[0])} />
                <Badge variant="outline" className="text-[9px] px-1">{phase}</Badge>
                {msg.strategy && (
                  <span className="text-[9px] text-muted-foreground">{msg.strategy}</span>
                )}
                <span className="flex-1" />
                {msg.author_name && (
                  <span className="text-[10px] font-medium">{msg.author_name}</span>
                )}
              </div>

              {/* Body (always show truncated, expand for full) */}
              {msg.body && (
                <div className="px-2 py-1.5 text-xs">
                  <p className={cn('whitespace-pre-wrap', !isExpanded && 'line-clamp-2')}>
                    {msg.body}
                  </p>
                </div>
              )}

              {/* Expanded details — structured sections */}
              {isExpanded && (
                <div className="px-2 py-1.5 border-t border-border space-y-2">
                  <StepSections sections={msg.step_entity_sections} fallback={msg.step_entity} />
                  <StepSections sections={msg.step_context_sections} fallback={msg.step_context} />
                  <StepSections sections={msg.step_rules_sections} fallback={msg.step_rules} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
